import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineController, LineElement, PointElement, Tooltip, Legend,
} from "chart.js";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import { corMedida, formatarNumero, formatarPercentualCurto, rotuloCurtoMes, temaGrafico } from "./analiseUtils";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineController, LineElement, PointElement, Tooltip, Legend);

/** Valor escrito acima de cada barra e o % acima de cada ponto da linha de conversão, como no modelo. */
const valoresNoGrafico = {
    id: "valoresNoGrafico",
    afterDatasetsDraw(chart, args, opcoes) {
        const { ctx } = chart;
        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        chart.data.datasets.forEach((dataset, di) => {
            const meta = chart.getDatasetMeta(di);
            if (meta.hidden) return;
            const ehLinha = dataset.type === "line";
            ctx.font = ehLinha ? "700 11px Roboto, Helvetica, Arial, sans-serif" : "600 10px Roboto, Helvetica, Arial, sans-serif";
            ctx.fillStyle = ehLinha ? opcoes.corLinha : opcoes.cor;
            meta.data.forEach((elemento, i) => {
                const valor = dataset.data[i];
                if (valor === null || valor === undefined || (!ehLinha && !valor)) return;
                const { x, y } = elemento.getProps(["x", "y"], true);
                const texto = ehLinha ? formatarPercentualCurto(valor) : formatarNumero(valor);
                if (ehLinha) {
                    // Fundo na cor da área do gráfico: o % da linha não se mistura com o número das barras
                    const largura = ctx.measureText(texto).width + 8;
                    ctx.fillStyle = opcoes.fundo;
                    ctx.beginPath();
                    ctx.roundRect(x - largura / 2, y - 23, largura, 15, 4);
                    ctx.fill();
                    ctx.fillStyle = opcoes.corLinha;
                }
                ctx.fillText(texto, x, y - (ehLinha ? 10 : 3));
            });
        });
        ctx.restore();
    },
};

/**
 * Evolução mês a mês: fichas acionadas, atendimentos e negociações (barras, contagens no eixo da esquerda) e a
 * conversão do mês (linha, % no eixo da direita).
 */
export default function GraficoEvolucao({ meses, fichasMes, atendimentosMes, negociacoesMes, conversaoMes }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const tema = temaGrafico(darkMode);
    const corLinha = darkMode ? "#e6e6e6" : "#344767";

    const serie = (rotulo, dados, medida) => ({
        label: rotulo,
        data: dados,
        backgroundColor: corMedida(medida, darkMode),
        borderColor: tema.superficie,
        borderWidth: { left: 1, right: 1 },
        borderRadius: { topLeft: 4, topRight: 4 },
        borderSkipped: "bottom",
        barPercentage: 0.85,
        categoryPercentage: 0.7,
        maxBarThickness: 36,
        yAxisID: "y",
        order: 2,
    });

    const maximo = Math.max(...fichasMes, ...atendimentosMes, ...(negociacoesMes || []), 1);
    const maiorConversao = Math.max(...(conversaoMes || []).filter(v => v !== null && v !== undefined), 0);

    const data = {
        labels: meses.map(rotuloCurtoMes),
        datasets: [
            serie("Fichas acionadas", fichasMes, "fichas"),
            serie("Atendimentos", atendimentosMes, "atendimentos"),
            serie("Negociações", negociacoesMes || [], "negociacoes"),
            {
                type: "line",
                label: "Conversão (%)",
                data: conversaoMes || [],
                borderColor: corLinha,
                backgroundColor: corLinha,
                borderWidth: 2,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointBorderColor: tema.superficie,
                pointBorderWidth: 2,
                tension: 0,
                spanGaps: true,
                yAxisID: "y1",
                order: 1,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        layout: { padding: { top: 8 } },
        plugins: {
            legend: {
                position: "top",
                align: "center",
                labels: {
                    color: tema.texto, usePointStyle: true, pointStyle: "circle", boxWidth: 8, boxHeight: 8, padding: 14, font: { size: 12 },
                    // Na ordem do gráfico (o Chart.js ordena pela camada de desenho, que põe a linha primeiro)
                    sort: (a, b) => a.datasetIndex - b.datasetIndex,
                },
            },
            tooltip: {
                callbacks: {
                    label: (ctx) => (ctx.dataset.yAxisID === "y1"
                        ? `${ctx.dataset.label}: ${ctx.parsed.y === null ? "–" : formatarPercentualCurto(ctx.parsed.y)}`
                        : `${ctx.dataset.label}: ${formatarNumero(ctx.parsed.y)}`),
                },
            },
            valoresNoGrafico: { cor: tema.texto, corLinha, fundo: tema.superficie },
        },
        scales: {
            x: {
                grid: { display: false },
                border: { color: tema.grade },
                ticks: { color: tema.textoSecundario, font: { size: 12 } },
            },
            y: {
                beginAtZero: true,
                suggestedMax: maximo * 1.15, // espaço para o valor no topo das barras
                grid: { color: tema.grade },
                border: { display: false },
                ticks: { color: tema.textoSecundario, font: { size: 11 }, callback: (v) => formatarNumero(v) },
            },
            y1: {
                position: "right",
                beginAtZero: true,
                // Eixo de % até 1,6 vez a maior conversão (no mínimo 10%), para a linha não colar no topo
                suggestedMax: Math.min(Math.max(maiorConversao * 1.6, 0.1), 1),
                grid: { drawOnChartArea: false },
                border: { display: false },
                ticks: { color: tema.textoSecundario, font: { size: 11 }, callback: (v) => formatarPercentualCurto(v) },
            },
        },
    };

    return (
        <MDBox height="300px">
            <Bar data={data} options={options} plugins={[valoresNoGrafico]} />
        </MDBox>
    );
}
